import React from 'react'
import { makeStyles } from '@material-ui/core/styles'
import Button from '../Button/Button'
import Typography from '../Typography/Typography'
import style from './style'

const useStyles = makeStyles(style)

/** Props for controlled, one-based pagination. */
export type PaginationType = {
    /** Current page, starting at 1. */
    page: number
    /** Positive number of items per page. */
    pageSize: number
    totalCount: number
    onPageChange: (page: number) => void
    itemLabel?: string
    previousLabel?: string
    nextLabel?: string
    loading?: boolean
    disabled?: boolean
    className?: string
    'aria-label'?: string
    'data-testid'?: string
}

/** Displays a result range and navigation without fetching or slicing data. */
const Pagination = ({
    page,
    pageSize,
    totalCount,
    onPageChange,
    itemLabel = 'items',
    previousLabel = 'Previous',
    nextLabel = 'Next',
    loading = false,
    disabled = false,
    className = '',
    'aria-label': ariaLabel = 'Pagination',
    'data-testid': dataTestId = 'pagination'
}: PaginationType): JSX.Element => {
    const classes = useStyles()
    const count = Math.max(0, totalCount)
    const size = Math.max(1, pageSize)
    const pageCount = Math.max(1, Math.ceil(count / size))
    const currentPage = Math.min(Math.max(1, page), pageCount)
    const firstItem = count === 0 ? 0 : (currentPage - 1) * size + 1
    const lastItem = Math.min(currentPage * size, count)
    const controlsDisabled = disabled || loading || count === 0

    return (
        <nav
            className={`${classes.pagination} ${className}`}
            aria-label={ariaLabel}
            aria-busy={loading}
            data-testid={dataTestId}
        >
            <div role='status' aria-live='polite' aria-atomic='true'>
                <Typography variant='body3' color='neutral-500'>
                    {`Showing ${firstItem}–${lastItem} of ${count} ${itemLabel}`}
                </Typography>
            </div>
            <div className={classes.controls}>
                <Button
                    type='button'
                    variant='outlined'
                    color='neutral'
                    disabled={controlsDisabled || currentPage === 1}
                    onClick={() => onPageChange(currentPage - 1)}
                    data-testid={`${dataTestId}-previous`}
                >
                    {previousLabel}
                </Button>
                <Button
                    type='button'
                    variant='outlined'
                    color='neutral'
                    disabled={controlsDisabled || currentPage === pageCount}
                    onClick={() => onPageChange(currentPage + 1)}
                    data-testid={`${dataTestId}-next`}
                >
                    {nextLabel}
                </Button>
            </div>
        </nav>
    )
}

export default Pagination
